import axios, { AxiosHeaders, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { ERROR_CODES } from '../utils/errorHandler';
import { checkRateLimit } from './rateLimiter';

// Security headers configuration - simplified for CORS debugging
const SECURITY_HEADERS = {
  'X-Requested-With': 'XMLHttpRequest',
  // Temporarily disabled for CORS debugging:
  // 'X-Content-Type-Options': 'nosniff',
  // 'X-Frame-Options': 'DENY',
  // 'X-XSS-Protection': '1; mode=block',
  // 'Strict-Transport-Security': 'max-age=31536000; includeSubDomains',
  // 'Content-Security-Policy': "default-src 'self'; script-src 'self' 'unsafe-inline' 'unsafe-eval'; style-src 'self' 'unsafe-inline'; img-src 'self' data:; font-src 'self' data:; connect-src 'self';",
};

// Get CSRF token from cookie
const getCSRFToken = (): string | null => {
  const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

// Add security headers to requests - simplified for CORS debugging
const addSecurityHeaders = (config: InternalAxiosRequestConfig) => {
  // Skip for external URLs
  if (!config.url?.startsWith('/')) {
    return config;
  }

  // Create new headers instance
  const headers = new AxiosHeaders();
  
  // Copy existing headers
  if (config.headers) {
    Object.entries(config.headers).forEach(([key, value]) => {
      if (value !== undefined) {
        headers.set(key, value);
      }
    });
  }
  
  // Add minimal security headers
  headers.set('X-Requested-With', 'XMLHttpRequest');
  
  // Update config with new headers
  config.headers = headers;

  return config;
};

// Rate limiting interceptor - temporarily disabled for CORS debugging
const handleRateLimit = async (config: InternalAxiosRequestConfig) => {
  // Skip rate limiting for login requests and non-API requests
  if (!config.url?.startsWith('/api/') || config.url?.includes('/auth/login')) {
    return config;
  }

  // Get client IP (in a real app, this would come from your server)
  const clientIp = 'client-ip-placeholder';
  const rateLimit = checkRateLimit(clientIp);

  if (!rateLimit.allowed) {
    const error = new Error('Rate limit exceeded');
    (error as any).code = ERROR_CODES.RATE_LIMIT_EXCEEDED;
    (error as any).retryAfter = rateLimit.retryAfter;
    throw error;
  }

  return config;
};

// Response interceptor for security headers
const handleSecurityHeaders = (response: AxiosResponse) => {
  // Security headers are handled by the server, no need to warn about missing ones
  return response;
};

// Error interceptor
export const handleSecurityError = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;

    // Handle common security-related status codes
    switch (status) {
      case 401:
        // Handle unauthorized
        break;
      case 403:
        // Handle forbidden
        break;
      case 429:
        // Handle rate limiting
        const retryAfter = error.response?.headers['retry-after'];
        if (retryAfter) {
          console.warn(`Rate limited. Retry after: ${retryAfter} seconds`);
        }
        break;
    }
  }

  return Promise.reject(error);
};

// Configure axios instance with security middleware
export const secureAxios = axios.create({
  timeout: 10000, // 10 seconds
  // withCredentials: true, // Temporarily disabled for CORS debugging
  // xsrfCookieName: 'XSRF-TOKEN',
  // xsrfHeaderName: 'X-XSRF-TOKEN',
});

// Add request interceptors
secureAxios.interceptors.request.use(addSecurityHeaders);
secureAxios.interceptors.request.use(handleRateLimit, (error) =>
  Promise.reject(error)
);

// Add response interceptors
secureAxios.interceptors.response.use(handleSecurityHeaders, handleSecurityError);

// Export configured axios instance as default
export default secureAxios;
